import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Check, Mail, Phone, Shield, Zap } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui";

type Step = "identifier" | "otp" | "success";
type InputType = "email" | "phone" | "unknown";

export const Route = createFileRoute("/login/")({
	component: LoginPage,
});

function LoginPage() {
	const [step, setStep] = useState<Step>("identifier");
	const [identifier, setIdentifier] = useState("");
	const [otp, setOtp] = useState(["", "", "", "", "", ""]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const otpInputs = useRef<(HTMLInputElement | null)[]>([]);
	const [countdown, setCountdown] = useState(0);
	const [canResend, setCanResend] = useState(true);

	const getInputType = useCallback((): InputType => {
		const value = identifier.trim();
		if (!value) return "unknown";
		if (/^[\d\s\-+()]+$/.test(value) && value.replace(/\D/g, "").length >= 7) {
			return "phone";
		}
		if (value.includes("@")) {
			return "email";
		}
		return "unknown";
	}, [identifier]);

	const inputType = getInputType();

	const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
	const isValidPhone =
		/^[\d\s\-+()]+$/.test(identifier) &&
		identifier.replace(/\D/g, "").length >= 10;
	const isValidInput = isValidEmail || isValidPhone;
	const isOtpComplete = otp.every((digit) => digit.length === 1);

	const startResendCountdown = useCallback(() => {
		setCanResend(false);
		setCountdown(30);
	}, []);

	useEffect(() => {
		if (countdown > 0) {
			const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
			return () => clearTimeout(timer);
		}
		if (countdown === 0 && !canResend) {
			setCanResend(true);
		}
	}, [countdown, canResend]);

	const handleSendOtp = async () => {
		if (!isValidInput) return;

		setError("");
		setIsLoading(true);

		try {
			const isPhone = isValidPhone && !isValidEmail;
			const endpoint = isPhone
				? "/api/auth/send-phone-otp"
				: "/api/auth/send-email-otp";
			const body = isPhone
				? { phoneNumber: identifier }
				: { email: identifier };

			const response = await fetch(endpoint, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				const data = await response.json();
				throw new Error(data.error || "Failed to send verification code");
			}

			setStep("otp");
			startResendCountdown();
			setTimeout(() => otpInputs.current[0]?.focus(), 100);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to send code");
		} finally {
			setIsLoading(false);
		}
	};

	const handleResendOtp = async () => {
		if (!canResend) return;
		setOtp(["", "", "", "", "", ""]);
		await handleSendOtp();
	};

	const handleOtpInput = (index: number, value: string) => {
		const cleanValue = value.replace(/\D/g, "");

		if (cleanValue.length > 1) {
			// Handle paste
			const digits = cleanValue.split("").slice(0, 6);
			const newOtp = [...digits, ...Array(6 - digits.length).fill("")];
			setOtp(newOtp);
			const nextIndex = Math.min(digits.length, 5);
			otpInputs.current[nextIndex]?.focus();
		} else {
			const newOtp = [...otp];
			newOtp[index] = cleanValue;
			setOtp(newOtp);
			if (cleanValue && index < 5) {
				otpInputs.current[index + 1]?.focus();
			}
		}
	};

	const handleOtpKeydown = (index: number, event: React.KeyboardEvent) => {
		if (event.key === "Backspace" && !otp[index] && index > 0) {
			otpInputs.current[index - 1]?.focus();
		}
		if (event.key === "ArrowLeft" && index > 0) {
			otpInputs.current[index - 1]?.focus();
		}
		if (event.key === "ArrowRight" && index < 5) {
			otpInputs.current[index + 1]?.focus();
		}
	};

	const handleVerifyOtp = async () => {
		if (!isOtpComplete) return;

		setError("");
		setIsLoading(true);

		try {
			const isPhone = isValidPhone && !isValidEmail;
			const otpCode = otp.join("");

			const endpoint = isPhone
				? "/api/auth/phone-number/verify"
				: "/api/auth/sign-in/email-otp";
			const body = isPhone
				? { phoneNumber: identifier, code: otpCode }
				: { email: identifier, otp: otpCode };

			const response = await fetch(endpoint, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				const data = await response.json();
				throw new Error(data.error || "Invalid verification code");
			}

			setStep("success");
			setTimeout(() => {
				window.location.href = "/";
			}, 800);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Verification failed");
		} finally {
			setIsLoading(false);
		}
	};

	const handleBackToIdentifier = () => {
		setStep("identifier");
		setOtp(["", "", "", "", "", ""]);
		setError("");
	};

	return (
		<div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-bg-primary p-8">
			{/* Ambient background effects */}
			<div className="-translate-x-1/2 pointer-events-none absolute top-[20%] left-1/2 h-[600px] w-[600px] animate-[ambientPulse_8s_ease-in-out_infinite] bg-[radial-gradient(circle,rgba(20,184,166,0.08)_0%,transparent_70%)]" />
			<div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(20,184,166,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(20,184,166,0.015)_1px,transparent_1px)] bg-[size:60px_60px]" />

			<div
				className={`relative z-10 w-full max-w-[420px] animate-[cardEnter_0.6s_ease-smooth_forwards] rounded-2xl border border-border bg-bg-secondary p-10 shadow-[0_0_0_1px_rgba(20,184,166,0.05),0_20px_50px_-20px_rgba(0,0,0,0.5),0_0_100px_-50px_rgba(20,184,166,0.15)]${step === "success" ? "border-accent shadow-[0_0_0_1px_rgba(20,184,166,0.3),0_20px_50px_-20px_rgba(0,0,0,0.5),0_0_60px_-20px_rgba(20,184,166,0.4)]" : ""}
				`}
			>
				{/* Brand */}
				<div className="mb-8 flex items-center gap-3.5 border-border border-b pb-6">
					<div className="flex h-11 w-11 items-center justify-center rounded-xl border border-accent/20 bg-accent/10 shadow-[0_0_20px_-5px_rgba(20,184,166,0.3)]">
						<Zap className="h-6 w-6 text-accent" />
					</div>
					<div className="flex flex-col">
						<span className="font-sans font-semibold text-base text-text-primary">
							Customer Portal
						</span>
						<span className="font-mono text-[10px] text-text-tertiary uppercase tracking-[0.1em]">
							Secure Access
						</span>
					</div>
				</div>

				{step === "identifier" && (
					<div className="animate-[stepEnter_0.4s_ease-smooth_forwards]">
						<div className="mb-7">
							<h1 className="mb-2 font-sans font-semibold text-2xl text-text-primary">
								Sign in
							</h1>
							<p className="text-sm text-text-secondary leading-relaxed">
								Enter your email or phone to receive a one-time code
							</p>
						</div>

						<form
							onSubmit={(e) => {
								e.preventDefault();
								handleSendOtp();
							}}
							className="flex flex-col gap-5"
						>
							<div className="flex flex-col gap-2">
								<label
									htmlFor="identifier"
									className="font-medium text-[13px] text-text-secondary"
								>
									Email or phone number
								</label>
								<div
									className={`relative flex items-center rounded-[10px] border bg-bg-elevated transition-all duration-200 focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgba(20,184,166,0.1),0_0_20px_-5px_rgba(20,184,166,0.3)]${isValidInput ? "border-accent/40" : "border-border"}
									`}
								>
									<div className="flex pl-3.5 text-text-tertiary transition-colors duration-200 focus-within:text-accent">
										{inputType === "phone" ? (
											<Phone className="h-5 w-5" />
										) : (
											<Mail className="h-5 w-5" />
										)}
									</div>
									<input
										type="text"
										id="identifier"
										value={identifier}
										onChange={(e) => setIdentifier(e.target.value)}
										placeholder="you@company.com or +1 234 567 8900"
										autoComplete="email tel"
										className="flex-1 border-none bg-transparent p-3.5 text-[15px] text-text-primary outline-none placeholder:text-text-tertiary"
										disabled={isLoading}
									/>
									{isValidInput && (
										<div className="animate-[checkPop_0.3s_ease-bounce] pr-3.5 text-accent">
											<Check className="h-5 w-5" />
										</div>
									)}
								</div>
							</div>

							{error && (
								<div className="flex items-center gap-2 rounded-lg border border-bug/20 bg-bug/10 px-4 py-3 text-[13px] text-bug">
									{error}
								</div>
							)}

							<Button
								type="submit"
								variant="primary"
								size="lg"
								loading={isLoading}
								disabled={!isValidInput}
								className="w-full"
							>
								{isLoading ? "Sending code..." : "Send verification code"}
							</Button>
						</form>

						<div className="mt-6 flex items-center justify-center gap-2 border-border border-t pt-6 text-text-tertiary text-xs">
							<Shield className="h-4 w-4" />
							<span>Secured with passwordless authentication</span>
						</div>
					</div>
				)}

				{step === "otp" && (
					<div className="animate-[stepSlideIn_0.4s_ease-smooth_forwards]">
						<div className="mb-7">
							<button
								type="button"
								onClick={handleBackToIdentifier}
								className="mb-4 inline-flex cursor-pointer items-center gap-1.5 border-none bg-transparent py-1 text-[13px] text-text-tertiary transition-colors hover:text-text-secondary"
							>
								<ArrowLeft className="h-4 w-4" />
								Back
							</button>
							<h1 className="mb-2 font-sans font-semibold text-2xl text-text-primary">
								Enter code
							</h1>
							<p className="text-sm text-text-secondary leading-relaxed">
								We sent a 6-digit code to{" "}
								<span className="font-mono text-[13px] text-accent">
									{identifier}
								</span>
							</p>
						</div>

						<form
							onSubmit={(e) => {
								e.preventDefault();
								handleVerifyOtp();
							}}
							className="flex flex-col gap-5"
						>
							<div className="flex flex-col gap-3">
								<div className="font-medium text-[13px] text-text-secondary">
									Verification code
								</div>
								<div className="flex justify-between gap-2">
									{otp.map((digit, i) => (
										<input
											key={i}
											type="text"
											inputMode="numeric"
											maxLength={6}
											ref={(el) => {
												otpInputs.current[i] = el;
											}}
											value={digit}
											onChange={(e) => handleOtpInput(i, e.target.value)}
											onKeyDown={(e) => handleOtpKeydown(i, e)}
											className={`focus:-translate-y-0.5 h-[60px] w-[52px] rounded-[10px] border bg-bg-elevated text-center font-mono font-semibold text-2xl text-text-primary caret-accent outline-none transition-all duration-200 focus:border-accent focus:shadow-[0_0_0_3px_rgba(20,184,166,0.1),0_0_20px_-5px_rgba(20,184,166,0.4)] disabled:opacity-50${digit ? "border-accent/30 bg-accent/5" : "border-border"}
											`}
											disabled={isLoading}
											autoComplete="one-time-code"
										/>
									))}
								</div>
								<div className="mt-1 text-center">
									{canResend ? (
										<button
											type="button"
											onClick={handleResendOtp}
											className="cursor-pointer border-none bg-transparent text-[13px] text-accent transition-opacity hover:underline hover:opacity-80"
										>
											Didn't receive it? Resend code
										</button>
									) : (
										<span className="font-mono text-[13px] text-text-tertiary">
											Resend available in {countdown}s
										</span>
									)}
								</div>
							</div>

							{error && (
								<div className="flex items-center gap-2 rounded-lg border border-bug/20 bg-bug/10 px-4 py-3 text-[13px] text-bug">
									{error}
								</div>
							)}

							<Button
								type="submit"
								variant="primary"
								size="lg"
								loading={isLoading}
								disabled={!isOtpComplete}
								className="w-full"
							>
								{isLoading ? "Verifying..." : "Verify & Sign in"}
							</Button>
						</form>
					</div>
				)}

				{step === "success" && (
					<div className="flex animate-[stepEnter_0.4s_ease-smooth_forwards] flex-col items-center py-4 text-center">
						<div className="mb-5 flex h-[72px] w-[72px] animate-[successPulse_0.6s_ease-bounce] items-center justify-center rounded-full border-2 border-accent bg-accent/10 text-accent shadow-[0_0_40px_-10px_rgba(20,184,166,0.5)]">
							<Check className="h-12 w-12" />
						</div>
						<h1 className="mb-2 font-sans font-semibold text-text-primary text-xl">
							Welcome back
						</h1>
						<p className="text-sm text-text-tertiary">
							Redirecting to dashboard...
						</p>
					</div>
				)}
			</div>

			{/* Footer */}
			<div className="absolute bottom-8 flex items-center gap-2 font-mono text-text-tertiary text-xs">
				<span className="opacity-70">System Status: </span>
				<span className="h-1.5 w-1.5 animate-[statusPulse_2s_ease-in-out_infinite] rounded-full bg-status-done" />
				<span className="text-status-done">Operational</span>
			</div>
		</div>
	);
}
